import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import Box from '../../../components/Box';
import { Checkbox, Col, Form, Input, Row, Select } from 'antd';
import { initialAccountDevices } from './constants';
import FormItemNoMarginStyled from '../../../components/FormItem/FormItemNoMarginStyled';
import SelectDevices from './SelectDevices';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import Text from 'components/Text';

type IAddAccountButtonProps = {
  handleAddAccount: (values: any) => Promise<boolean>;
  lstTimeZone: any[],
};

export const NOTE = ['Website', 'Digital Signage'];

const AddAccountButton: React.FC<IAddAccountButtonProps> = ({ handleAddAccount, lstTimeZone }) => {
  const { Option } = Select;
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const watchDevices = Form.useWatch('devices', form);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const onFinish = async () => {
    try {
      const values = await form.validateFields();
      const boolean = await handleAddAccount(values);
      if (boolean) {
        hideModal();
      }
    } catch (error) {
    }
  };

  const onClickNote = (note: string) => {
    const currentNote = form.getFieldValue('note');

    form.setFieldValue(
      'note',
      currentNote ? `${currentNote}, ${note}` : note
    );
  };

  const formatBitBlocker = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 48);
    return digits.match(/.{1,6}/g)?.join('-') || '';
  };

  return (
    <AddAccountButtonStyled>
      <Button variant="PRIMARY" icon="add" onClick={showModal}>
        Add Account
      </Button>
      <Modal
        open={isModalVisible}
        onCancel={hideModal}
        onOk={onFinish}
        title={'Add Account'}
        closeIcon={null}
        width={1200}
        centered>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ devices: initialAccountDevices }}>
          <Box px={1}>
            <Row>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="salon_name" label="Salon’s Name"
                  rules={
                    [
                      { required: true }
                    ]
                  }
                >
                  <Input size='large' autoComplete='off' placeholder='Please enter...' />
                </FormItemNoMarginStyled>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="street" label="Street"
                  rules={
                    [
                      { required: true }
                    ]
                  }>
                  <Input size='large' autoComplete='off' placeholder='Please enter...' />
                </FormItemNoMarginStyled>
              </Col>
            </Row>

            <Row>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <Row>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled
                      name="firsName"
                      label="Firs Name"
                      rules={
                        [
                          { required: true }
                        ]
                      }>
                      <Input size='large' autoComplete='off' placeholder='Please enter...' />
                    </FormItemNoMarginStyled>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled
                      name="lastName"
                      label="Last Name"
                      rules={
                        [
                          { required: true }
                        ]
                      }>
                      <Input size='large' autoComplete='off' placeholder='Please enter...' />
                    </FormItemNoMarginStyled>
                  </Col>
                </Row>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="city" label="City" rules={
                  [
                    { required: true }
                  ]
                }>
                  <Input size='large' autoComplete='off' placeholder='Please enter...' />
                </FormItemNoMarginStyled>
              </Col>
            </Row>
            <Row>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled
                  name="phone"
                  label="Phone"
                  rules={
                    [
                      { required: true }
                    ]
                  }>
                  <Input size='large' type="number" autoComplete='off' placeholder='Please enter...' />
                </FormItemNoMarginStyled>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="state" label="State" rules={
                  [
                    { required: true }
                  ]
                }>
                  <Input size='large' autoComplete='off' placeholder='Please enter...' />
                </FormItemNoMarginStyled>
              </Col>
            </Row>

            <Row>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="email" label="Email" rules={
                  [
                    { required: true },
                    {
                      type: 'email',
                      message: 'The input is not valid email',
                    }
                  ]
                }>
                  <Input size='large' autoComplete='off' placeholder='Please enter...' />
                </FormItemNoMarginStyled>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="zip" label="Zip Code" rules={
                  [
                    { required: true }
                  ]
                }>
                  <Input size='large' autoComplete='off' placeholder='Please enter...' />
                </FormItemNoMarginStyled>
              </Col>
            </Row>

            <Row>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="password" label="Password"
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (!value || value.length >= 6) {
                          return Promise.resolve();
                        }
                        return Promise.reject('Password must be at least 6 characters long.');
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="Please enter..."
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </FormItemNoMarginStyled>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled name="time_zone" label="Timezone"
                  rules={
                    [
                      { required: true }
                    ]
                  }>
                  <Select size='large' showSearch placeholder="Select">
                    {lstTimeZone.map((tz, index) => (
                      <Option key={index} value={tz.value}>
                        {tz.name}
                      </Option>
                    ))}
                  </Select>
                </FormItemNoMarginStyled>
              </Col>
            </Row>

            <Row>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <SelectDevices watchDevices={watchDevices} />
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                <FormItemNoMarginStyled
                  name="bitBlocker"
                  label="Bit Blocker # (48 digits total)"
                  rules={[
                    // {
                    //   required: true,
                    //   message: 'Please enter Bit Blocker',
                    // },
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }

                        const digits = (value || '').replace(/-/g, '');

                        if (digits.length !== 48) {
                          return Promise.reject(
                            new Error('Bit Blocker must contain 48 digits'),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="000000-000000-000000-000000-000000-000000-000000-000000"
                    maxLength={55}
                    inputMode="numeric"
                    onChange={(e) => {
                      const formattedValue = formatBitBlocker(e.target.value);
                      form.setFieldValue('bitBlocker', formattedValue);
                    }}
                    size="large"
                  />
                </FormItemNoMarginStyled>
              </Col>
            </Row>
            <Row>
              <Col xs={{ span: 24 }}>
                <FormItemNoMarginStyled name="note" label="Note">
                  <Input.TextArea size='large' autoComplete='off' placeholder='Please enter...' rows={3}/>
                </FormItemNoMarginStyled>
                <RecommendedReason>
                  {NOTE.map((item) => (
                    <RecommendedReasonItem key={item} onClick={() => onClickNote(item)}>
                      <Text variant='BODY_2' color='blue' style={{ fontWeight: 500 }}>
                        {item}
                      </Text>
                    </RecommendedReasonItem>
                  ))}
                </RecommendedReason>
              </Col>
            </Row>
          </Box>
        </Form>
      </Modal>
    </AddAccountButtonStyled >
  );
};

export default AddAccountButton;
const AddAccountButtonStyled = styled.div``;

const FormItemCheckBoxStyled = styled(Form.Item)`
  margin: 10px;
  && .ant-form-item-label label {
    color: transparent;
  }
`;

const RecommendedReason = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  padding-left: 10px;
`;

const RecommendedReasonItem = styled.div`
  display: flex;
  height: 32px;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 100px;
  border: 1px solid #2563eb;
  background: #eef4ff;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    background-color: #dbeafe;
  }
`;
